import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, LoadingBlock } from "@/components/Common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, UploadCloud, ShieldAlert, Database } from "lucide-react";
import { toast } from "sonner";

export default function Backup() {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const [info, setInfo] = useState(null);
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState("merge");
    const fileRef = useRef(null);
    const [result, setResult] = useState(null);

    const load = () => api.get("/backup/info").then((r) => setInfo(r.data));
    useEffect(() => {
        load();
    }, []);

    const handleRestore = async () => {
        const f = fileRef.current?.files?.[0];
        if (!f) return toast.error("Choose a backup .json file");
        const fd = new FormData();
        fd.append("file", f);
        setBusy(true);
        try {
            const { data } = await api.post(`/backup/restore?mode=${mode}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            toast.success(`Restore complete (${mode})`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Restore failed");
        } finally {
            setBusy(false);
        }
    };

    if (!info) return <LoadingBlock rows={6} />;

    return (
        <>
            <PageHeader
                eyebrow="Data portability"
                title="Backup & Restore"
                subtitle="Export your operational data as a JSON snapshot, or restore from a previous export."
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Download className="w-4 h-4" /> Export
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="text-sm text-muted-foreground">
                            Downloads a JSON file with all hotel collections — rooms, reservations, guests, tasks, reviews, sensors, chat history, and audit events. Use this for off-site backup or migration.
                        </div>
                        <a
                            href={`${BACKEND_URL}/api/backup/export`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="backup-export-link"
                        >
                            <Button data-testid="backup-export">
                                <Download className="w-4 h-4 mr-1" /> Download full backup
                            </Button>
                        </a>
                        <div className="rounded-lg border border-border p-3 bg-secondary/40">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                                <Database className="w-3.5 h-3.5" /> Collection counts
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-1.5 text-sm">
                                {Object.entries(info.counts).map(([k, v]) => (
                                    <div key={k} className="flex items-center justify-between pr-3">
                                        <span className="text-muted-foreground">{k}</span>
                                        <span className="tabular font-semibold" data-testid={`backup-count-${k}`}>{v}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <UploadCloud className="w-4 h-4" /> Restore
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="rounded-lg border border-[hsl(var(--warning)/0.35)] bg-[hsl(var(--warning)/0.08)] p-3 text-xs flex gap-2">
                            <ShieldAlert className="w-4 h-4 mt-0.5 text-[hsl(var(--warning))]" />
                            <div>
                                <div className="font-medium">Use with care</div>
                                <div className="text-muted-foreground">
                                    <span className="font-medium">Merge</span> upserts by id (safe).{" "}
                                    <span className="font-medium">Replace</span> wipes each collection first.
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                            <Select value={mode} onValueChange={setMode}>
                                <SelectTrigger className="w-40" data-testid="backup-mode">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="merge">Merge</SelectItem>
                                    <SelectItem value="replace">Replace</SelectItem>
                                </SelectContent>
                            </Select>
                            <input
                                ref={fileRef}
                                type="file"
                                accept=".json,application/json"
                                className="text-sm"
                                data-testid="backup-file"
                            />
                            <Button onClick={handleRestore} disabled={busy} data-testid="backup-restore">
                                <UploadCloud className="w-4 h-4 mr-1" /> {busy ? "Restoring…" : "Restore"}
                            </Button>
                        </div>
                        {result && (
                            <div className="text-sm rounded-lg bg-secondary/50 p-3" data-testid="backup-restore-result">
                                <div className="font-medium mb-1">Restore summary ({result.mode})</div>
                                <div className="text-xs text-muted-foreground space-y-0.5">
                                    {Object.entries(result.restored).map(([k, v]) => (
                                        <div key={k}>
                                            {k}: <span className="tabular">{v}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </>
    );
}
